import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useState } from 'react';

const SupportPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: "How does OhMyGAWD work?",
      answer: "OhMyGAWD uses advanced AI technology to provide personalized guidance based on your questions. Simply type your question, and our oracle will generate a thoughtful response drawing from vast knowledge and spiritual wisdom traditions."
    },
    {
      question: "What's the difference between free and subscription access?",
      answer: "Free users can ask 10 questions per month to try the service. Basic subscribers ($9.95/month) get 100 queries per month with extended history and all tone personalities. Premium subscribers ($19.95/month) get 500 queries per month with unlimited history access, custom characters, and advanced analytics."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes! You can cancel your subscription at any time from your account settings. There are no cancellation fees, and you'll retain access until the end of your current billing period."
    },
    {
      question: "Is my data private and secure?",
      answer: "Absolutely. We take your privacy seriously and follow OECD AI Principles. All data is encrypted, and we never sell your personal information. Read our Privacy Policy for complete details."
    },
    {
      question: "What are the different character personalities?",
      answer: "You can customize your oracle's tone and style. Choose from Mystic (ethereal and poetic), Sage (wise and philosophical), Oracle (direct and insightful), and more. Each character provides unique perspectives while maintaining accuracy."
    },
    {
      question: "Can OhMyGAWD provide medical or legal advice?",
      answer: "No. OhMyGAWD is for entertainment and inspiration only. It should never replace professional advice from doctors, therapists, lawyers, or other licensed experts. Always consult qualified professionals for serious matters."
    },
    {
      question: "How do I access my query history?",
      answer: "Subscribers can view their complete query history in their profile dashboard. You can search, filter, and export your past questions and responses at any time."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, debit cards, and digital payment methods through our secure payment processor. Your payment information is never stored on our servers."
    }
  ];

  return (
    <div className="min-h-screen relative">
      {/* Mystical Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/3315dd06c3e67642f54e90e5d56d0279.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.85
        }}
      />

      {/* Gradient Overlay for better text readability */}
      <div 
        className="fixed inset-0 z-0 bg-gradient-to-b from-purple-900/20 via-transparent to-pink-900/20"
      />

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white drop-shadow-lg mb-6">
                How Can We Help?
              </h1>
              <p className="text-white/90 drop-shadow text-lg max-w-2xl mx-auto">
                Find answers to common questions or reach out to our support team for personalized assistance.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mb-6">
                  <i className="ri-mail-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-purple-900 mb-3">Email Support</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Send us a message and we'll respond within 24 hours.
                </p>
                <a 
                  href="mailto:support@cybernetix.com" 
                  className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2 cursor-pointer"
                >
                  support@cybernetix.com
                  <i className="ri-arrow-right-line"></i>
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mb-6">
                  <i className="ri-question-answer-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-purple-900 mb-3">FAQ</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Browse our frequently asked questions for quick answers.
                </p>
                <a 
                  href="#faq" 
                  className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2 cursor-pointer"
                >
                  View FAQs
                  <i className="ri-arrow-down-line"></i>
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mb-6">
                  <i className="ri-book-open-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-purple-900 mb-3">Documentation</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Learn more about OhMyGAWD's features and capabilities.
                </p>
                <a 
                  href="#docs" 
                  className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2 cursor-pointer"
                >
                  Read Docs
                  <i className="ri-arrow-right-line"></i>
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              id="faq"
              className="mb-16"
            >
              <h2 className="font-serif text-4xl font-bold text-white drop-shadow-lg mb-8 text-center">
                Frequently Asked Questions
              </h2>
              <div className="max-w-3xl mx-auto space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-purple-50 transition-colors cursor-pointer"
                    >
                      <h4 className="font-semibold text-lg text-purple-900 pr-4">
                        {faq.question}
                      </h4>
                      <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-purple-600 flex-shrink-0`}></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-purple-800/95 to-purple-900/95 backdrop-blur-sm rounded-3xl p-12 text-center text-white shadow-xl"
            >
              <h2 className="font-serif text-4xl font-bold mb-4">
                Still Have Questions?
              </h2>
              <p className="text-purple-200 text-lg mb-8 max-w-2xl mx-auto">
                Our support team is here to help. Reach out and we'll get back to you as soon as possible.
              </p>
              <a 
                href="mailto:support@cybernetix.com"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-purple-900 font-semibold rounded-full hover:shadow-2xl hover:shadow-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                Contact Support
                <i className="ri-arrow-right-line"></i>
              </a>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default SupportPage;